import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useSSEStream from './useSSEStream';
import { useAuth } from '../contexts/AuthContext';

/**
 * useAiComplianceReview
 *
 * Reviewer-side hook that POSTs to the AI compliance review SSE
 * endpoint and surgically patches the `useReviewerCampaign` cache as
 * per-cell verdicts stream back.
 *
 * Wire-shape contract (must match `backend/api/reviews_api.py`
 * `stream_ai_compliance_review`):
 *
 *   POST /api/reviews/campaigns/{id}/ai-review/stream
 *     body: { config_ids: [...] }
 *
 *   POST /api/reviews/campaigns/{id}/compliance-tail/stream
 *     body: { config_ids: [...], expected_cells?: number }
 *
 *   SSE frames:
 *     - start:                    { expected_cells, config_ids }
 *     - status:                   { completion_percentage, message, ... }
 *     - compliance_decision_update:
 *         { data: [{ contact_membership_id, config_id, compliance: {...} }, ...] }
 *     - end:                      { succeeded, failed, expected_cells }
 *     - error:                    { message }
 *
 * UX contract (per the locked plan):
 *   - No optimistic UI. Cells flip live as `compliance_decision_update`
 *     events arrive (no shimmer, no greyscale — single hop from
 *     "yellow" / "amber" / current state to the AI verdict chip).
 *   - Modal closes instantly on `start`; progress lives in the toolbar
 *     pill driven by `progress` from this hook.
 *   - No mid-run cancel UI for the MVP. The hook still exposes `stop`
 *     so the page can clean up on unmount, but there is no user-facing
 *     button.
 *
 * Cache-merge pattern is reused verbatim from
 * `useReviewerDecisionMutation` so the surface area for "how do AI
 * verdicts land in the cache" stays identical to per-cell writes.
 */

const PROGRESS_FLOOR = 1; // start frame paints a non-zero pill so the
//                         user sees we kicked off even before the
//                         first runner status update lands.

export function useAiComplianceReview(campaignId, { onToast } = {}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const queryKey =
    user?.id && campaignId
      ? ['reviewerCampaign', user.id, campaignId]
      : null;

  const [progress, setProgress] = useState(0);
  const [expectedCells, setExpectedCells] = useState(0);
  // Latest progress kept in a ref so the SSE callbacks (created once
  // by useSSEStream) don't race against the `progress` state setter.
  const progressRef = useRef(0);
  // ``fetchEventSource`` closes over the ``onComplete`` from the render
  // when the stream *started* — state like ``expectedCells`` can still be
  // 0 there even after the ``start`` frame raised it. Read counts from
  // refs at completion time instead.
  const expectedCellsRef = useRef(0);
  const reviewedCellsRef = useRef(0);

  const patchCellsIntoCache = useCallback(
    (items) => {
      if (!queryKey || !Array.isArray(items) || items.length === 0) return;
      const current = queryClient.getQueryData(queryKey);
      if (!current || !Array.isArray(current.contacts)) return;

      // Index incoming items by contact for one O(N+M) merge pass
      // instead of a per-item linear scan over contacts.
      const byContact = new Map();
      for (const item of items) {
        const cmid = item?.contact_membership_id;
        const cfg = item?.config_id;
        const compliance = item?.compliance;
        if (!cmid || cfg == null || cfg === '' || !compliance) continue;
        const cfgKey = String(cfg);
        const cmidKey = String(cmid);
        if (!byContact.has(cmidKey)) byContact.set(cmidKey, []);
        byContact.get(cmidKey).push({ cfg: cfgKey, compliance });
      }
      if (byContact.size === 0) return;

      const nextContacts = current.contacts.map((row) => {
        const rowCmid = row.contact_membership_id != null ? String(row.contact_membership_id) : '';
        const updates = rowCmid ? byContact.get(rowCmid) : undefined;
        if (!updates) return row;
        const nextDrafts = { ...(row.drafts || {}) };
        for (const { cfg, compliance } of updates) {
          const cell = nextDrafts[cfg] || {};
          nextDrafts[cfg] = { ...cell, compliance };
        }
        return { ...row, drafts: nextDrafts };
      });

      queryClient.setQueryData(queryKey, {
        ...current,
        contacts: nextContacts,
      });
    },
    [queryClient, queryKey],
  );

  /**
   * Patch the template_review field on the matching draft in the React
   * Query cache when a template_review_update SSE event arrives.
   * Shape: { config_id, verdict?, compliant?, reasoning, reviewed_at, reviewed_by,
 *   is_stale?, reviewed_* snapshots }
   */
  const patchTemplateReviewIntoCache = useCallback(
    (payload) => {
      if (!queryKey || !payload || !payload.config_id) return;
      const current = queryClient.getQueryData(queryKey);
      if (!current || !Array.isArray(current.drafts)) return;

      const verdict = payload.verdict ?? null;
      let compliantLegacy = payload.compliant;
      if (compliantLegacy === undefined || compliantLegacy === null) {
        if (verdict === 'compliant') compliantLegacy = true;
        else if (verdict === 'non_compliant' || verdict === 'needs_human_review') {
          compliantLegacy = false;
        }
      }

      const nextDrafts = current.drafts.map((draft) => {
        if (String(draft.config_id || '') !== String(payload.config_id || '')) return draft;
        const prevTr = draft.template_review || {};
        return {
          ...draft,
          template_review: {
            ...prevTr,
            verdict,
            compliant: compliantLegacy ?? null,
            reasoning: payload.reasoning ?? null,
            reviewed_at: payload.reviewed_at ?? null,
            reviewed_by: payload.reviewed_by ?? prevTr.reviewed_by ?? 'ai',
            is_stale: payload.is_stale ?? false,
            reviewed_subject_template:
              payload.reviewed_subject_template !== undefined
                ? payload.reviewed_subject_template
                : prevTr.reviewed_subject_template,
            reviewed_body_template:
              payload.reviewed_body_template !== undefined
                ? payload.reviewed_body_template
                : prevTr.reviewed_body_template,
            reviewed_user_prompt:
              payload.reviewed_user_prompt !== undefined
                ? payload.reviewed_user_prompt
                : prevTr.reviewed_user_prompt,
          },
        };
      });

      queryClient.setQueryData(queryKey, { ...current, drafts: nextDrafts });
    },
    [queryClient, queryKey],
  );

  const handleData = useCallback(
    (frame) => {
      if (!frame || typeof frame !== 'object') return;

      if (frame.event === 'start') {
        const expected = Number(frame.data?.expected_cells || 0);
        expectedCellsRef.current = expected;
        reviewedCellsRef.current = 0;
        setExpectedCells(expected);
        // Paint a non-zero pill on kickoff so the reviewer immediately
        // sees the AI is working even before the first status update.
        progressRef.current = expected > 0 ? PROGRESS_FLOOR : 100;
        setProgress(progressRef.current);
        return;
      }

      if (frame.event === 'template_review_update') {
        patchTemplateReviewIntoCache(frame.data || {});
        return;
      }

      if (frame.event === 'compliance_decision_update') {
        const batch = frame.data?.data || frame.data || [];
        if (Array.isArray(batch)) {
          reviewedCellsRef.current += batch.length;
        }
        patchCellsIntoCache(batch);
      }
    },
    [patchCellsIntoCache, patchTemplateReviewIntoCache],
  );

  const handleStatus = useCallback((statusPayload) => {
    const pct = Number(statusPayload?.completion_percentage);
    if (Number.isFinite(pct)) {
      // Status updates come from one runner per config; clamp upward
      // so a slow runner can't drag the pill backward.
      const next = Math.max(progressRef.current, Math.min(100, pct));
      progressRef.current = next;
      setProgress(next);
    }
  }, []);

  const handleComplete = useCallback(() => {
    progressRef.current = 100;
    setProgress(100);
    const applied = reviewedCellsRef.current;
    const planned = expectedCellsRef.current;
    if (queryKey) {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.refetchQueries({ queryKey, type: 'active' });
    }
    let description;
    if (applied > 0) {
      description = `Applied AI verdicts to ${applied.toLocaleString()} ${
        applied === 1 ? 'cell' : 'cells'
      }.`;
    } else if (planned > 0) {
      description =
        'Review finished. No verdict updates arrived for this run — refresh the page if the table still looks wrong.';
    } else {
      description =
        'Review finished. Nothing in this selection was eligible for AI review.';
    }
    onToast?.({
      type: 'success',
      title: 'AI compliance review complete',
      description,
    });
    // Reset the chip after a short hold so the reviewer sees the "done"
    // state without it lingering forever.
    setTimeout(() => {
      progressRef.current = 0;
      setProgress(0);
      setExpectedCells(0);
      expectedCellsRef.current = 0;
      reviewedCellsRef.current = 0;
    }, 1500);
  }, [onToast, queryClient, queryKey]);

  const handleError = useCallback(
    (err) => {
      progressRef.current = 0;
      setProgress(0);
      expectedCellsRef.current = 0;
      reviewedCellsRef.current = 0;
      setExpectedCells(0);
      onToast?.({
        type: 'error',
        title: 'AI review failed',
        description:
          err?.message ||
          'Could not run the AI review. Please try again in a moment.',
      });
    },
    [onToast],
  );

  const { isStreaming, startStream, stopStream } = useSSEStream({
    onStatus: handleStatus,
    onData: handleData,
    onComplete: handleComplete,
    onError: handleError,
  });

  /**
   * Kick off an AI review for the given draft config_ids.
   * Returns the `startStream` promise (resolves when the SSE channel
   * closes — modals don't await this; they fire-and-forget).
   */
  const start = useCallback(
    ({ configIds }) => {
      if (!Array.isArray(configIds) || configIds.length === 0) {
        return Promise.resolve();
      }
      if (isStreaming) {
        // Guarded at the modal trigger as well, but defensive here so
        // a runaway double-click can't open two streams.
        return Promise.resolve();
      }
      return startStream(
        `/api/reviews/campaigns/${campaignId}/ai-review/stream`,
        {
          method: 'POST',
          body: { config_ids: configIds },
        },
      );
    },
    [campaignId, isStreaming, startStream],
  );

  /**
   * Subscribe-only SSE after async template resolution (bulk / Phase 2).
   */
  const observeComplianceTail = useCallback(
    ({ configIds, expectedCells = 0 }) => {
      if (!Array.isArray(configIds) || configIds.length === 0) {
        return Promise.resolve();
      }
      if (isStreaming) {
        return Promise.resolve();
      }
      return startStream(
        `/api/reviews/campaigns/${campaignId}/compliance-tail/stream`,
        {
          method: 'POST',
          body: { config_ids: configIds, expected_cells: expectedCells },
        },
      );
    },
    [campaignId, isStreaming, startStream],
  );

  return {
    start,
    observeComplianceTail,
    stop: stopStream,
    isRunning: isStreaming,
    progress,
    expectedCells,
  };
}

export default useAiComplianceReview;
